<script setup>
import axios from '@axios'
import moment from 'moment-timezone'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

/*
 * RM-359 — Pending RM Review: the RM Team's review page.
 *
 * Requests that wait for RM before they go to the restaurant (the one rule:
 * lib/reservationReview), oldest first, with the deterministic signals RM
 * decides on. Three decisions — the same an RM Team phone makes by text:
 *   Approve       → the exact request the member authorized goes to the
 *                   restaurant now. The deadline the member was given at
 *                   submit does not move (RM's review used part of it);
 *   Needs changes → the card hold is released and the request goes back to the
 *                   member as a Draft with what RM requires;
 *   Decline       → not sent; the hold is released; the member is told, and
 *                   the request goes back to Draft (no "not approved" status).
 * Group/private inquiries never wait here: a restaurant that needs review
 * stays on the inquiry and its copy goes to preferred@ to review and forward.
 * RM never edits a request here. The preview link is view-only.
 *
 * The approval email's buttons land here as ?request=<id>&action=approve|changes:
 * that request's confirmation opens, ready to confirm. The link is not a
 * credential — this page needs an RM Team sign-in and nothing happens until
 * the confirmation is pressed.
 */

const route = useRoute()

const loading = ref(false)
const loadError = ref('')
const waiting = ref([])
const recent = ref([])
const showRecent = ref(false)

const load = async () => {
  loading.value = true
  loadError.value = ''
  try {
    const { data } = await axios.get('/reservation/approvals')
    waiting.value = data.waiting || []
    recent.value = data.recent || []
  } catch (error) {
    loadError.value = error?.response?.data?.error || 'Couldn\'t load the requests. Try again'
  } finally {
    loading.value = false
  }
}

// From the approval email's buttons: open that request's confirmation.
const linkNotice = ref('')

const openFromLink = async () => {
  const id = typeof route.query.request === 'string' ? route.query.request : ''
  const action = typeof route.query.action === 'string' ? route.query.action : ''
  if (!id) return
  // Once: a reload must not reopen it. The address is cleaned in place — a
  // router navigation here would re-key the page and drop the dialog.
  window.history.replaceState(window.history.state, '', route.path)
  const row = waiting.value.find(r => r.reservation?._id === id)
  if (!row) {
    const done = recent.value.find(r => r.reservation?._id === id)

    linkNotice.value = done
      ? `That request is no longer waiting: ${done.approval.memberName} → ${waitsFor(done)} · ${decisionLine(done)}`
      : 'That request is no longer waiting for approval.'

    return
  }
  await nextTick()
  document.getElementById(`approval-${row.approval._id}`)?.scrollIntoView({ block: 'start' })
  if (ACTION_COPY[action]) open(row, action)
}

// RM-359 (Kapil 10/29): whether members see the Likelihood of Acceptance. Off
// until RM turns it on; every score posts to #request-scores in Slack either way.
const likelihoodSetting = ref(null) // { membersSee, channel }
const likelihoodSaving = ref(false)
const likelihoodError = ref('')

const loadLikelihoodSetting = async () => {
  try {
    const { data } = await axios.get('/reservation/approvals/likelihood-setting')
    likelihoodSetting.value = data
  } catch (error) {
    likelihoodSetting.value = null
  }
}

const saveLikelihoodSetting = async on => {
  likelihoodSaving.value = true
  likelihoodError.value = ''
  try {
    const { data } = await axios.post('/reservation/approvals/likelihood-setting', { membersSee: !!on })
    likelihoodSetting.value = data
  } catch (error) {
    likelihoodError.value = error?.response?.data?.error || 'Couldn\'t save. Try again'
  } finally {
    likelihoodSaving.value = false
  }
}

onMounted(async () => {
  loadLikelihoodSetting()
  await load()
  await openFromLink()
})

// ── one decision at a time ────────────────────────────────────────────────
const confirming = ref(null) // { row, action }
const feedback = ref('')
const note = ref('')
const deciding = ref(false)
const decideError = ref('')
const doneMessage = ref('')

const ACTION_COPY = {
  approve: { title: 'Approve and send', button: 'Approve and send', color: 'primary-dark' },
  changes: { title: 'Send back for changes', button: 'Send to member', color: 'primary-dark' },
  decline: { title: 'Decline', button: 'Decline - don\'t send', color: 'error' },
}

const open = (row, action) => {
  confirming.value = { row, action }
  feedback.value = ''
  note.value = ''
  decideError.value = ''
}

const canConfirm = computed(() => {
  if (!confirming.value) return false
  if (confirming.value.action === 'changes') return feedback.value.trim().length > 0

  return true
})

const decide = async () => {
  if (!canConfirm.value) return
  const { row, action } = confirming.value
  deciding.value = true
  decideError.value = ''
  try {
    const { data } = await axios.post(`/reservation/${row.reservation._id}/approval`, {
      action,
      feedback: action === 'changes' ? feedback.value.trim() : (note.value.trim() || undefined),
    })
    const who = `${row.approval.memberName}'s request`
    const retrying = data?.holdReleased === false ? ' The card hold couldn\'t be released just now - it is being retried.' : ''
    doneMessage.value = {
      approve: `Approved ${who} - sent to ${waitsFor(row)}.`,
      changes: `Sent ${who} back to ${firstName(row)} for changes.${retrying}`,
      decline: `Declined ${who} - not sent to ${waitsFor(row)}.${retrying}`,
    }[action]
    if (data?.holdExpired) doneMessage.value = `Not sent: ${firstName(row)}'s card hold had expired. ${firstName(row)} has been asked to submit again to authorize a new hold.`
    if (data?.autoDeclined) doneMessage.value = `Approved ${who} - every restaurant on it auto-declines ${firstName(row)}, so it was not sent.`
    confirming.value = null
    await load()
  } catch (error) {
    decideError.value = error?.response?.data?.error || 'That didn\'t go through. Try again'
  } finally {
    deciding.value = false
  }
}

// ── formatting ─────────────────────────────────────────────────────────────
function restaurantNames(r) {
  const names = [r?.preferredRestaurant, ...((r?.alternateRestaurants || []).filter(n => typeof n === 'string'))].filter(Boolean)

  return names.length ? names.join(', ') : 'the restaurant'
}

// The restaurants this approval is about.
function waitsFor(row) {
  return restaurantNames(row.reservation)
}

function firstName(row) {
  return String(row.approval.memberName || 'the member').split(' ')[0]
}

function money(n) {
  return `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
}

function requestLine(r) {
  if (!r) return ''
  const parts = []
  const day = moment(r.date, 'YYYY-MM-DD', true)
  if (day.isValid()) parts.push(day.format('ddd M/D'))
  if (r.preferredTime) parts.push(r.preferredTime)
  else if (r.earliestTime && r.latestTime) parts.push(`${r.earliestTime}-${r.latestTime}`)
  if (r.partySize) parts.push(`party of ${r.partySize}`)
  if (r.minSpendPerPerson && r.partySize) parts.push(`${money(r.minSpendPerPerson * r.partySize)} F&B (${money(r.minSpendPerPerson)}pp)`)

  return parts.join(' · ')
}

function waitingLabel(row) {
  const m = row.waitingReviewMinutes
  if (m == null) return ''
  if (m < 60) return `${m} min in review hours`
  const h = Math.floor(m / 60)

  return `${h}h ${m % 60}m in review hours`
}

function overTarget(row) {
  return (row.waitingReviewMinutes || 0) >= 120
}

const FIELD_LABELS = {
  additionalNotes: 'Note to restaurant',
  specialRequests: 'Special requests',
  occasionDetails: 'Occasion details',
}

// The member's category, whose setting at the restaurant decided (lib/reservationReview).
const CATEGORY_LABELS = { star: 'Star', verified: 'Verified', regular: 'Regular' }

// What moved a request's Likelihood of Acceptance (lib/requestLikelihood factors).
const LIKELIHOOD_FACTORS = {
  restaurant_rate: 'restaurant\'s acceptance rate via RM',
  overall_rate: 'all restaurants\' acceptance rate via RM (too few here)',
  no_rate: 'no RM record yet',
  premium_commitment: 'a specific premium order in the note',
  member_favorite: 'Member Favorite, little RM record yet',
  star: 'Star member',
  verified: 'Verified member',
  spend_2x: 'spend 2x the minimum',
  spend_1_5x: 'spend 1.5x the minimum',
  party_5_plus: 'party of 5 or more',
  prior_accepted: 'accepted there before',
  preferred_member: 'Preferred Member there',
  restaurant_history: 'restaurant\'s acceptance rate via RM',
  restaurant_preferred: 'Preferred restaurant, little RM record yet',
  restaurant_none_accepted: 'none of its requests accepted',
}

function likelihoodLine(likelihood) {
  const moved = (likelihood.factors || [])
    .filter(f => f.points !== 0)
    .map(f => `${f.points == null ? '' : `${f.points > 0 ? '+' : '\u2212'}${Math.abs(f.points)} `}${LIKELIHOOD_FACTORS[f.key] || f.key}${f.rate == null ? '' : ` (${f.rate}%, ${f.accepted} of ${f.decided})`}`)

  return moved.length ? `${likelihood.band}: ${moved.join(' · ')}` : `${likelihood.band}: nothing for or against it yet`
}

function restaurantSignal(r) {
  const who = CATEGORY_LABELS[r.category] ? `${CATEGORY_LABELS[r.category]} members: review` : 'review'
  const parts = [r.preferred ? 'Preferred' : 'Not Preferred', `${who} ${r.review}${r.reviewSource === 'default' ? ' (default)' : ''}`]
  if (r.floorPerPerson) parts.push(`floor ${money(r.floorPerPerson)}pp`)
  if (r.history) parts.push(`${r.history.requests} requests · ${r.history.accepted} accepted via RM`)

  return parts.join(' · ')
}

function decisionLine(row) {
  const d = row.approval.decision || {}
  const what = {
    approved: 'Approved', declined: 'Declined', needs_changes: 'Sent back for changes', expired: 'Not reviewed in time', hold_expired: 'Approved, but the card hold had expired - not sent', withdrawn: 'Withdrawn by the member',
  }[row.approval.status] || row.approval.status
  const who = d.byName || d.by
  const when = d.at ? moment(d.at).tz('America/New_York').format('ddd M/D h:mma [ET]') : ''

  return [what, who ? `by ${who}` : null, d.channel === 'sms' ? 'by text' : null, when].filter(Boolean).join(' · ')
}
</script>

<template>
  <div class="approvals">
    <header class="approvals__header">
      <h1 class="approvals__title">
        Pending RM Review
      </h1>
      <p class="approvals__sub">
        Requests RM reviews before they go to the restaurant. Oldest first. The restaurant sees nothing until you approve.
      </p>
    </header>

    <VAlert
      v-if="doneMessage"
      type="success"
      variant="tonal"
      closable
      class="mb-4"
      @click:close="doneMessage = ''"
    >
      {{ doneMessage }}
    </VAlert>
    <VAlert
      v-if="linkNotice"
      type="info"
      variant="tonal"
      closable
      class="mb-4"
      @click:close="linkNotice = ''"
    >
      {{ linkNotice }}
    </VAlert>
    <VAlert
      v-if="loadError"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ loadError }}
    </VAlert>

    <div
      v-if="loading && !waiting.length"
      class="approvals__skeletons"
    >
      <VSkeletonLoader
        v-for="i in 2"
        :key="i"
        type="article"
        class="mb-3"
      />
    </div>

    <p
      v-else-if="!waiting.length"
      class="approvals__empty"
    >
      Nothing is waiting for approval.
    </p>

    <article
      v-for="row in waiting"
      :id="`approval-${row.approval._id}`"
      :key="row.approval._id"
      class="approval-card"
    >
      <div class="approval-card__top">
        <span
          class="approval-card__wait"
          :class="{ 'approval-card__wait--late': overTarget(row) }"
        >{{ waitingLabel(row) }}</span>
      </div>

      <h2 class="approval-card__headline">
        {{ row.approval.memberName }} → {{ waitsFor(row) }}
      </h2>
      <p class="approval-card__request">
        {{ requestLine(row.reservation) }}
        <span v-if="row.reservation?.reservationType === 'private'"> · group/private</span>
      </p>
      <p
        v-if="row.approval.expiresLabel"
        class="approval-card__why"
      >
        Expires {{ row.approval.expiresLabel }} (set when {{ firstName(row) }} submitted - approving doesn't move it)
      </p>
      <p class="approval-card__why">
        {{ row.approval.ruleLabel }}<span v-if="row.approval.submission > 1"> · resubmitted ({{ row.approval.submission }})</span>
      </p>

      <!-- restaurant-facing text, with what fired -->
      <div
        v-for="(text, field) in row.signals?.freeText || {}"
        :key="field"
        class="approval-card__note"
      >
        <span class="approval-card__label">{{ FIELD_LABELS[field] || field }}</span>
        <span>"{{ text }}"</span>
      </div>
      <p
        v-if="row.approval.asks?.length"
        class="approval-card__asks"
      >
        Asks: {{ row.approval.asks.map(a => `"${a.match}"`).join(', ') }}
      </p>

      <dl class="approval-card__signals">
        <template
          v-for="r in row.signals?.restaurants || []"
          :key="r.restaurantId"
        >
          <dt>{{ r.name }}</dt>
          <dd>{{ restaurantSignal(r) }}</dd>
        </template>
        <dt>{{ firstName(row) }}</dt>
        <dd>
          {{ CATEGORY_LABELS[row.signals?.category] || (row.signals?.star ? 'Star' : 'Regular') }} member · Preferred Member {{ row.signals?.preferredMember ? 'yes' : 'no' }} ·
          {{ row.signals?.member?.bookings ?? 0 }} bookings / {{ row.signals?.member?.requests ?? 0 }} requests via RM
        </dd>
        <!-- RM-359 PR 3: the Likelihood of Acceptance recorded at submit, and what moved it (Kapil 10/27: reasons, not a bare total) -->
        <template v-if="row.signals?.likelihood?.band">
          <dt>Likelihood</dt>
          <dd>{{ likelihoodLine(row.signals.likelihood) }}</dd>
        </template>
        <template v-if="row.approval.memberChanges?.length">
          <dt>Changed</dt>
          <dd>{{ row.approval.memberChanges.map(c => c.field).join(', ') }}</dd>
        </template>
        <template v-if="row.reservation?.stripeHoldConfirmedAmount">
          <dt>Card hold</dt>
          <dd>{{ money(row.reservation.stripeHoldConfirmedAmount) }}, placed at submit</dd>
        </template>
      </dl>

      <a
        v-if="row.previewUrl"
        :href="row.previewUrl"
        target="_blank"
        rel="noopener"
        class="approval-card__preview"
      >See what the restaurant will receive</a>

      <div class="approval-card__actions">
        <VBtn
          color="primary-dark"
          size="large"
          class="approval-card__approve"
          @click="open(row, 'approve')"
        >
          Approve
        </VBtn>
        <VBtn
          variant="tonal"
          size="large"
          @click="open(row, 'changes')"
        >
          Needs changes
        </VBtn>
        <VBtn
          variant="text"
          color="error"
          size="large"
          @click="open(row, 'decline')"
        >
          Decline
        </VBtn>
      </div>
    </article>

    <section
      v-if="recent.length"
      class="approvals__recent"
    >
      <button
        class="approvals__recent-toggle"
        @click="showRecent = !showRecent"
      >
        {{ showRecent ? 'Hide' : 'Show' }} recent decisions ({{ recent.length }})
      </button>
      <ul
        v-if="showRecent"
        class="approvals__recent-list"
      >
        <li
          v-for="row in recent"
          :key="row.approval._id"
        >
          {{ row.approval.memberName }} → {{ waitsFor(row) }}
          <div class="approvals__recent-meta">
            {{ decisionLine(row) }}
          </div>
          <div
            v-if="row.approval.decision?.feedback"
            class="approvals__recent-meta"
          >
            "{{ row.approval.decision.feedback }}"
          </div>
        </li>
      </ul>
    </section>

    <section
      v-if="likelihoodSetting"
      class="approvals__setting"
    >
      <VSwitch
        :model-value="likelihoodSetting.membersSee"
        :loading="likelihoodSaving"
        :disabled="likelihoodSaving"
        label="Members see the Likelihood of Acceptance"
        color="primary"
        density="compact"
        hide-details
        inset
        @update:model-value="saveLikelihoodSetting"
      />
      <p class="approvals__setting-note">
        {{ likelihoodSetting.membersSee ? 'On: members see it before they submit, in the app, RM AI and the texted review.' : 'Off: members don\'t see it.' }}
        Every request's score posts to {{ likelihoodSetting.channel }} in Slack either way.
      </p>
      <p
        v-if="likelihoodError"
        class="approvals__dialog-error"
      >
        {{ likelihoodError }}
      </p>
    </section>

    <VDialog
      :model-value="!!confirming"
      max-width="460"
      @update:model-value="v => { if (!v) confirming = null }"
    >
      <VCard v-if="confirming">
        <VCardText>
          <h3 class="approvals__dialog-title">
            {{ ACTION_COPY[confirming.action].title }}
          </h3>
          <p class="approvals__dialog-body">
            {{ confirming.row.approval.memberName }} → {{ waitsFor(confirming.row) }}<br>
            {{ requestLine(confirming.row.reservation) }}
          </p>
          <p
            v-if="confirming.action === 'approve'"
            class="approvals__dialog-body"
          >
            It goes to the restaurant now, exactly as {{ firstName(confirming.row) }} submitted it.
            <template v-if="confirming.row.approval.expiresLabel">
              It still expires {{ confirming.row.approval.expiresLabel }}.
            </template>
          </p>
          <template v-else-if="confirming.action === 'changes'">
            <p class="approvals__dialog-body">
              The card hold is released and the request goes back to {{ firstName(confirming.row) }} with this, from RM. {{ firstName(confirming.row) }} has to make the change before it can be sent:
            </p>
            <VTextarea
              v-model="feedback"
              class="approvals__input"
              label="What needs to change?"
              rows="3"
              auto-grow
              maxlength="1200"
              autofocus
            />
          </template>
          <template v-else>
            <p class="approvals__dialog-body">
              Nothing goes to the restaurant, the card hold is released and {{ firstName(confirming.row) }} is told. The request goes back to their drafts.
            </p>
            <VTextarea
              v-model="note"
              class="approvals__input"
              label="A note for them (optional)"
              rows="2"
              auto-grow
              maxlength="1200"
            />
          </template>
          <p
            v-if="decideError"
            class="text-error approvals__dialog-error"
          >
            {{ decideError }}
          </p>
          <VBtn
            block
            size="large"
            :color="ACTION_COPY[confirming.action].color"
            :disabled="!canConfirm"
            :loading="deciding"
            class="mt-2"
            @click="decide"
          >
            {{ ACTION_COPY[confirming.action].button }}
          </VBtn>
          <VBtn
            block
            variant="text"
            class="mt-1"
            :disabled="deciding"
            @click="confirming = null"
          >
            Cancel
          </VBtn>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.approvals {
  max-width: 720px;
  margin: 0 auto;
  padding: 16px;
}

.approvals__header {
  margin-bottom: 16px;
}

.approvals__title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
}

.approvals__sub {
  margin: 4px 0 0;
  color: #555;
  font-size: 14px;
  line-height: 1.4;
}

.approvals__empty {
  padding: 32px 0;
  color: #555;
  font-size: 16px;
  text-align: center;
}

.approval-card {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  background: #fff;
}

.approval-card__top {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
}

.approval-card__wait {
  color: #666;
}

.approval-card__wait--late {
  color: rgb(var(--v-theme-error));
  font-weight: 600;
}

.approval-card__headline {
  margin: 8px 0 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
}

.approval-card__request {
  margin: 4px 0 0;
  font-size: 16px;
}

.approval-card__why {
  margin: 4px 0 0;
  color: #555;
  font-size: 14px;
}

.approval-card__note {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f6f6f6;
  font-size: 14px;
  line-height: 1.4;
}

.approval-card__label {
  color: #666;
  font-size: 12px;
  font-weight: 600;
}

.approval-card__asks {
  margin: 4px 0 0;
  color: #8a5a00;
  font-size: 12px;
}

.approval-card__signals {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 4px 12px;
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.4;
}

.approval-card__signals dt {
  color: #666;
  font-weight: 600;
}

.approval-card__signals dd {
  margin: 0;
}

.approval-card__preview {
  display: inline-block;
  margin-top: 12px;
  font-size: 14px;
}

.approval-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.approval-card__approve {
  flex: 1 1 160px;
}

.approvals__recent {
  margin-top: 24px;
}

.approvals__recent-toggle {
  color: var(--ds-brand-primary-dark);
  font-size: 14px;
  font-weight: 600;
}

.approvals__recent-list {
  padding: 0;
  margin: 8px 0 0;
  list-style: none;
  font-size: 14px;
}

.approvals__recent-list li {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.approvals__recent-meta {
  color: #666;
  font-size: 12px;
}

.approvals__setting {
  margin-top: 40px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.approvals__setting :deep(.v-label) {
  color: #333;
  font-size: 14px;
  opacity: 1;
}

.approvals__setting-note {
  margin: 4px 0 0;
  color: #666;
  font-size: 12px;
}

.approvals__dialog-title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 700;
}

.approvals__dialog-body {
  margin: 0 0 8px;
  font-size: 14px;
  line-height: 1.45;
}

/* iOS zooms into inputs under 16px */
.approvals__input :deep(textarea) {
  font-size: 16px;
}

.approvals__dialog-error {
  margin: 4px 0 0;
  font-size: 14px;
}

@media (max-width: 390px) {
  .approval-card__signals {
    grid-template-columns: 1fr;
    gap: 0 0;
  }

  .approval-card__signals dd {
    margin-bottom: 6px;
  }
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
  # RM-359: the RM Team's review of requests waiting for approval. Platform
  # admins only (the API's requireAdmin() is the real gate; this keeps the
  # page itself from loading for anyone else — H10).
  requiresAdmin: true
</route>
